import {ChangeDetectionStrategy, Component, inject, signal, WritableSignal} from '@angular/core';
import {PaymentService} from "../../services/payment/payment.service";
import {benefitsPageStyles, DebugService} from "../../services/debug.service";
import {ABService} from "../../services/a-b.service";
import {StorageService} from "../../services/storage.service";
import {environment} from "../../../../environments/environment";
import {Dialog} from "primeng/dialog";
import {ToggleSwitch} from "primeng/toggleswitch";
import {FormsModule} from "@angular/forms";
import {SelectButton} from "primeng/selectbutton";
import {ButtonDirective, ButtonLabel} from "primeng/button";
import {Textarea} from "primeng/textarea";
import {NgClass} from "@angular/common";
import {HttpClient} from "@angular/common/http";

@Component({
  selector: 'homey-debug-button',
  standalone: true,
  imports: [Dialog, ToggleSwitch, FormsModule, SelectButton, ButtonDirective, ButtonLabel, Textarea, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './debug-button.component.html',
})
export class DebugButtonComponent {
  protected readonly paymentService = inject(PaymentService);
  protected readonly debugService = inject(DebugService);
  protected readonly abService = inject(ABService);
  private readonly storageService = inject(StorageService);
  private readonly http = inject(HttpClient);
  protected readonly dialogOpen = signal(false);
  protected readonly enabled = environment.debug;

  protected readonly styleOptions = benefitsPageStyles.map(s => ({label: s, value: s}));
  protected readonly jsonEditorStates = new Map<string, WritableSignal<{ text: string; error: string }>>();

  protected getJsonEditorState(name: string, currentValue: unknown) {
    let state = this.jsonEditorStates.get(name);
    if (!state) {
      state = signal({text: JSON.stringify(currentValue, null, 2), error: ''});
      this.jsonEditorStates.set(name, state);
    }
    return state;
  }

  protected applyJsonVariable(v: { name: string; signal: WritableSignal<any>; validate: (input: unknown) => boolean }) {
    const state = this.jsonEditorStates.get(v.name);
    if (!state) return;

    const {text} = state();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      state.set({text, error: 'Invalid JSON'});
      return;
    }

    if (!v.validate(parsed)) {
      state.set({text, error: 'Type validation failed'});
      return;
    }

    v.signal.set(parsed);
    state.set({text, error: ''});
  }

  protected resetJsonVariable(v: { name: string; signal: WritableSignal<any> }) {
    const state = this.jsonEditorStates.get(v.name);
    if (state) {
      state.set({text: JSON.stringify(v.signal(), null, 2), error: ''});
    }
  }

  protected throwTestError() {
    throw new Error('Test error from debug button');
  }

  protected triggerHttpError() {
    this.http.get(`${environment.apiUrl}/api/this-endpoint-does-not-exist`).subscribe();
  }

  protected async clearStorage() {
    await this.storageService.clear();
    window.location.reload();
  }
}
